import { api } from '@/lib/api';
import type { Vessel } from './types';
import type { VesselFormValues, VesselUpdateValues } from './schema';

/**
 * Vessels REST client — thin wrapper over the shared axios instance.
 *
 * The backend exposes:
 *   GET    /vessels        list (filtered by RBAC scope server-side)
 *   GET    /vessels/:id    detail
 *   POST   /vessels        create
 *   PATCH  /vessels/:id    update (any subset of fields)
 *   DELETE /vessels/:id    soft-delete
 *
 * Errors flow through the api interceptor and become typed `ApiError`
 * instances carrying the backend i18n key (apiError.* in the locale
 * bundles).
 */

export const vesselsApi = {
  list: async (): Promise<Vessel[]> => {
    const { data } = await api.get<Vessel[]>('/vessels');
    return data;
  },

  getById: async (id: string): Promise<Vessel> => {
    const { data } = await api.get<Vessel>(`/vessels/${id}`);
    return data;
  },

  create: async (input: VesselFormValues): Promise<Vessel> => {
    const { data } = await api.post<Vessel>('/vessels', input);
    return data;
  },

  update: async (id: string, patch: VesselUpdateValues): Promise<Vessel> => {
    const { data } = await api.patch<Vessel>(`/vessels/${id}`, patch);
    return data;
  },

  remove: async (id: string): Promise<void> => {
    await api.delete(`/vessels/${id}`);
  },
};
